import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BookMarked, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/store/useAuthStore';
import { ApiError } from '@/api/client';
import {
  searchCatalog,
  createCatalogEntry,
  updateCatalogEntry,
  deleteCatalogEntry,
  type CatalogEntry,
  type CatalogEntryInput,
  type ConditionCategory,
} from '@/api/medical.api';
import {
  CATEGORY_LABELS,
  INHERITANCE_LABELS,
} from '@/components/PersonPanel/tabs/medical/medicalLabels';

// ─── Konstanten ─────────────────────────────────────────────────────────────

const CATEGORY_ORDER: ConditionCategory[] = ['disease', 'hereditary', 'cause_of_death'];

const CATEGORY_BADGE: Record<ConditionCategory, string> = {
  disease: 'bg-blue-50 text-blue-700 ring-blue-200',
  hereditary: 'bg-violet-50 text-violet-700 ring-violet-200',
  cause_of_death: 'bg-red-50 text-red-700 ring-red-200',
};

const INPUT_CLASS =
  'w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

// ─── Debounce-Hook ────────────────────────────────────────────────────────────

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Eintrags-Karte ───────────────────────────────────────────────────────────

interface EntryCardProps {
  entry: CatalogEntry;
  canManage: boolean;
  onEdit: (entry: CatalogEntry) => void;
  onDelete: (entry: CatalogEntry) => void;
}

function EntryCard({ entry, canManage, onEdit, onDelete }: EntryCardProps) {
  const meta: string[] = [];
  if (entry.icd10) meta.push(`ICD-10: ${entry.icd10}`);
  if (entry.geneSymbol) meta.push(`Gen: ${entry.geneSymbol}`);
  if (entry.inheritance) meta.push(INHERITANCE_LABELS[entry.inheritance] ?? entry.inheritance);
  if (entry.penetrance != null) meta.push(`Penetranz ${Math.round(entry.penetrance * 100)}%`);

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-800">{entry.label}</span>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${CATEGORY_BADGE[entry.category]}`}
          >
            {CATEGORY_LABELS[entry.category] ?? entry.category}
          </span>
          {entry.isSystem && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
              System
            </span>
          )}
        </div>

        {meta.length > 0 && (
          <p className="mt-1 text-xs text-slate-500">{meta.join(' · ')}</p>
        )}

        {entry.synonyms.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {entry.synonyms.map((s) => (
              <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                {s}
              </span>
            ))}
          </div>
        )}

        {entry.description && (
          <p className="mt-1.5 text-xs text-slate-400">{entry.description}</p>
        )}
      </div>

      {canManage && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => onEdit(entry)}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Eintrag bearbeiten"
            title="Bearbeiten"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {/* System-Einträge lassen sich nicht löschen (Backend lehnt es mit 400 ab). */}
          {!entry.isSystem && (
            <button
              type="button"
              onClick={() => onDelete(entry)}
              className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Eintrag löschen"
              title="Löschen"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Formular-Modal (Anlegen + Bearbeiten) ──────────────────────────────────

interface CatalogFormProps {
  entry?: CatalogEntry;
  onClose: () => void;
}

function CatalogForm({ entry, onClose }: CatalogFormProps) {
  const isEdit = !!entry;
  const queryClient = useQueryClient();

  const [label, setLabel] = useState(entry?.label ?? '');
  const [category, setCategory] = useState<ConditionCategory>(entry?.category ?? 'disease');
  const [synonyms, setSynonyms] = useState(entry?.synonyms.join(', ') ?? '');
  const [icd10, setIcd10] = useState(entry?.icd10 ?? '');
  const [geneSymbol, setGeneSymbol] = useState(entry?.geneSymbol ?? '');
  const [isHereditary, setIsHereditary] = useState(entry?.isHereditary ?? false);
  const [inheritance, setInheritance] = useState(entry?.inheritance ?? '');
  const [penetrance, setPenetrance] = useState(entry?.penetrance != null ? String(entry.penetrance) : '');
  const [description, setDescription] = useState(entry?.description ?? '');

  const onSuccess = (msg: string) => () => {
    queryClient.invalidateQueries({ queryKey: ['medical', 'catalog'] });
    toast.success(msg);
    onClose();
  };
  const onError = (error: ApiError) => toast.error(error.message || 'Speichern fehlgeschlagen.');

  const createMut = useMutation({
    mutationFn: (input: CatalogEntryInput) => createCatalogEntry(input),
    onSuccess: onSuccess('Eintrag angelegt.'),
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (input: CatalogEntryInput) => updateCatalogEntry(entry!.id, input),
    onSuccess: onSuccess('Eintrag gespeichert.'),
    onError,
  });

  const submitting = createMut.isPending || updateMut.isPending;

  // Penetranz muss – falls angegeben – zwischen 0 und 1 liegen.
  const penetranceNum = penetrance.trim() === '' ? null : Number(penetrance);
  const penetranceInvalid =
    penetranceNum != null && (Number.isNaN(penetranceNum) || penetranceNum < 0 || penetranceNum > 1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || penetranceInvalid) return;

    const input: CatalogEntryInput = {
      label: label.trim(),
      category,
      synonyms: synonyms
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      icd10: icd10.trim() || null,
      gene_symbol: geneSymbol.trim() || null,
      is_hereditary: isHereditary,
      // Backend lehnt inheritance ohne is_hereditary mit 422 ab → nur dann setzen.
      // Penetranz (Manifestations­wahrscheinlichkeit) ist ebenfalls nur bei
      // Erbkrankheiten sinnvoll – sonst bleibt bei abgewähltem „Erblich" ein
      // inkonsistenter Wert stehen.
      inheritance: isHereditary ? inheritance || null : null,
      penetrance: isHereditary ? penetranceNum : null,
      description: description.trim() || null,
    };

    if (isEdit) updateMut.mutate(input);
    else createMut.mutate(input);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? 'Eintrag bearbeiten' : 'Eintrag anlegen'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Bezeichnung <span className="text-red-500">*</span>
            </label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              autoFocus
              className={INPUT_CLASS}
              placeholder="z.B. Diabetes mellitus Typ 2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Kategorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ConditionCategory)}
              className={INPUT_CLASS}
            >
              {CATEGORY_ORDER.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Synonyme</label>
            <input
              value={synonyms}
              onChange={(e) => setSynonyms(e.target.value)}
              className={INPUT_CLASS}
              placeholder="Komma-getrennt, z.B. Zuckerkrankheit, T2DM"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">ICD-10</label>
              <input value={icd10} onChange={(e) => setIcd10(e.target.value)} className={INPUT_CLASS} placeholder="z.B. E11" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Gen-Symbol</label>
              <input
                value={geneSymbol}
                onChange={(e) => setGeneSymbol(e.target.value)}
                className={INPUT_CLASS}
                placeholder="z.B. BRCA1"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={isHereditary}
                onChange={(e) => setIsHereditary(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
              />
              Erblich
            </label>
          </div>

          {isHereditary && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Vererbungsmuster</label>
                <select
                  value={inheritance}
                  onChange={(e) => setInheritance(e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="">— unbestimmt —</option>
                  {Object.entries(INHERITANCE_LABELS).map(([value, lbl]) => (
                    <option key={value} value={value}>
                      {lbl}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Penetranz (0–1)</label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={penetrance}
                  onChange={(e) => setPenetrance(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="z.B. 0.8"
                />
                {penetranceInvalid && (
                  <p className="mt-1 text-xs text-red-500">Wert muss zwischen 0 und 1 liegen.</p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Beschreibung</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={INPUT_CLASS}
            />
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting || !label.trim() || penetranceInvalid}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Seite ────────────────────────────────────────────────────────────────────

export function MedicalCatalogPage() {
  const canManage = useAuthStore((s) => s.hasSensitivePermission('medical.manage'));
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [rawQuery, setRawQuery] = useState('');
  const [category, setCategory] = useState<ConditionCategory | 'all'>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogEntry | undefined>(undefined);

  const q = useDebounced(rawQuery.trim(), 300);

  const { data: entries, isLoading, isError, error } = useQuery<CatalogEntry[], ApiError>({
    queryKey: ['medical', 'catalog', q, category],
    queryFn: () =>
      searchCatalog({
        ...(q ? { q } : {}),
        ...(category !== 'all' ? { category } : {}),
      }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteCatalogEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'catalog'] });
      toast.success('Eintrag gelöscht.');
    },
    onError: (err: ApiError) => toast.error(err.message || 'Löschen fehlgeschlagen.'),
  });

  const isForbidden = isError && error instanceof ApiError && error.status === 403;

  const grouped = useMemo(() => {
    const map = new Map<ConditionCategory, CatalogEntry[]>();
    for (const e of entries ?? []) {
      const list = map.get(e.category) ?? [];
      list.push(e);
      map.set(e.category, list);
    }
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({ category: c, items: map.get(c)! }));
  }, [entries]);

  const openCreate = () => {
    setEditing(undefined);
    setFormOpen(true);
  };
  const openEdit = (entry: CatalogEntry) => {
    setEditing(entry);
    setFormOpen(true);
  };

  const handleDelete = async (entry: CatalogEntry) => {
    const ok = await confirm({
      title: 'Eintrag löschen',
      message: (
        <span>
          „{entry.label}" wird dauerhaft aus dem Katalog entfernt. Diese Aktion kann nicht rückgängig
          gemacht werden.
        </span>
      ),
      variant: 'danger',
      confirmLabel: 'Löschen',
    });
    if (ok) deleteMut.mutate(entry.id);
  };

  const isEmpty = !isLoading && !isError && (entries?.length ?? 0) === 0;
  const hasFilter = q !== '' || category !== 'all';

  return (
    <AppLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          {/* Kopf */}
          <div className="mb-1 flex items-center gap-2">
            <BookMarked className="h-5 w-5 text-slate-600" />
            <h1 className="text-2xl font-bold text-slate-800">Diagnose-Katalog</h1>
          </div>
          <p className="mb-6 text-xs text-slate-400">
            Kuratierbarer Katalog für Diagnosen, Erbkrankheiten und Todesursachen. System-Einträge sind
            vorgegeben und lassen sich nicht löschen, aber bearbeiten.
          </p>

          {/* Toolbar */}
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                placeholder="Katalog durchsuchen…"
                aria-label="Katalog durchsuchen"
                className={`${INPUT_CLASS} pl-8`}
              />
            </div>

            <div className="flex flex-wrap gap-1">
              {(['all', ...CATEGORY_ORDER] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    category === c
                      ? 'bg-slate-800 text-white'
                      : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {c === 'all' ? 'Alle' : CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>

            {canManage && (
              <button
                type="button"
                onClick={openCreate}
                className="flex shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Eintrag
              </button>
            )}
          </div>

          {/* Laden / Fehler */}
          {isLoading && (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" message="Katalog wird geladen…" />
            </div>
          )}
          {isForbidden && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              Keine Berechtigung für das Medizin-Modul. Der Diagnose-Katalog erfordert Zugriff auf das
              Gesundheitsmodul – bitte an eine Administratorin/einen Administrator wenden.
            </p>
          )}
          {isError && !isForbidden && (
            <p className="text-center text-sm text-red-500">
              Katalog konnte nicht geladen werden{error instanceof ApiError && error.message ? `: ${error.message}` : '.'}
            </p>
          )}

          {/* Leerzustand */}
          {isEmpty && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="text-sm text-slate-500">
                {hasFilter ? 'Keine Treffer.' : 'Keine Einträge — lege den ersten an.'}
              </p>
            </div>
          )}

          {/* Liste, gruppiert nach Kategorie */}
          {!isLoading && !isError && grouped.length > 0 && (
            <div className="space-y-6">
              {grouped.map(({ category: cat, items }) => (
                <section key={cat}>
                  <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                    {CATEGORY_LABELS[cat]}{' '}
                    <span className="font-normal text-slate-400">({items.length})</span>
                  </h2>
                  <div className="space-y-2">
                    {items.map((entry) => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        canManage={canManage}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      {canManage && formOpen && (
        <CatalogForm entry={editing} onClose={() => setFormOpen(false)} />
      )}
    </AppLayout>
  );
}

export default MedicalCatalogPage;
